/**
 * 权限码 —— 前后端唯一真相（架构书 §11、A-04）。
 *
 * 命名约定：全小写，点分段。多词段不加分隔符（沿用 Master Spec §4 的 `stockcount.approve`）。
 * 需要 step-up 提权的权限标记在 ELEVATED_PERMISSIONS 中。
 *
 * ⚠ 此文件是安全相关常量的唯一定义处。新增权限必须同时：
 *   1) 在此登记；2) 在 DEFAULT_ROLE_PERMISSIONS 中给出四个预置角色的映射；
 *   3) 在 packages/i18n 的 permission 命名空间补三语描述。
 */
export const PERMISSIONS = {
    // —— 仓库 ——
    WAREHOUSE_VIEW: 'warehouse.view',
    WAREHOUSE_MANAGE: 'warehouse.manage',
    // —— 布局 ——
    LAYOUT_EDIT: 'layout.edit',
    LAYOUT_LEASE_OVERRIDE: 'layout.lease.override',
    // —— 位置 ——
    LOCATION_ARCHIVE: 'location.archive',
    /** 【U-02】永久删除**已归档**位置。默认仅 ADMIN（不入 MANAGER_PERMISSIONS）；
        有任何历史引用（流水/余额/盘点）的位置永远拒绝删除，归档才是常规出口 */
    LOCATION_DELETE: 'location.delete',
    // —— 产品 ——
    PRODUCT_CREATE: 'product.create',
    PRODUCT_EDIT: 'product.edit',
    // —— 库存 ——
    INVENTORY_VIEW: 'inventory.view',
    INVENTORY_INBOUND: 'inventory.inbound',
    INVENTORY_OUTBOUND: 'inventory.outbound',
    INVENTORY_TRANSFER: 'inventory.transfer',
    INVENTORY_ADJUST: 'inventory.adjust',
    INVENTORY_RESERVE: 'inventory.reserve',
    /** 触发负库存的资格；同时也是"可作为委托授权人"的资格（D-02） */
    INVENTORY_OVERRIDE_NEGATIVE: 'inventory.override.negative',
    /** 手工改选非 FEFO 推荐批次（§15） */
    INVENTORY_OVERRIDE_FEFO: 'inventory.override.fefo',
    /** 报损 / 过期处理 —— I2c 的枚举例外流程之一（D-15） */
    INVENTORY_EXPIRED_DISPOSE: 'inventory.expired.dispose',
    // —— 订单 / 预留 ——
    ORDER_CREATE: 'order.create',
    ORDER_CONFIRM: 'order.confirm',
    ORDER_CANCEL: 'order.cancel',
    RESERVATION_RELEASE: 'reservation.release',
    // —— 供应商 ——
    SUPPLIER_VIEW: 'supplier.view',
    SUPPLIER_MANAGE: 'supplier.manage',
    // —— 盘点 ——
    STOCKCOUNT_CREATE: 'stockcount.create',
    STOCKCOUNT_APPROVE: 'stockcount.approve',
    // —— 预警 ——
    ALERT_RULE_MANAGE: 'alert.rule.manage',
    ALERT_DISMISS_CRITICAL: 'alert.dismiss_critical',
    ALERT_MANAGE_OTHERS: 'alert.manage_others',
    // —— 数据 ——
    IMPORT_EXECUTE: 'import.execute',
    EXPORT_DATA: 'export.data',
    REPORT_VIEW: 'report.view',
    /** M10 D-30 第 7 条：门禁导出文件里成本/价格列是否存在（不存在，不是留空） */
    REPORT_COST_VIEW: 'report.cost.view',
    // —— 系统 ——
    /** 全量审计（含登录/用户/角色/提权等系统级事件）——顶尖层专属 */
    AUDIT_VIEW: 'audit.view',
    /**
     * 【用户裁决 2026-08-17】运营版审计（"阉割版"）：能看到出入库、订单、
     * 产品等所有人的**业务**动作，但系统级事件（登录登出、改密、用户/角色/
     * 权限变更、提权记录）一律不可见。管理人员预设持有的是这一个。
     */
    AUDIT_VIEW_OPERATIONAL: 'audit.view.operational',
    USER_MANAGE: 'user.manage',
    /**
     * 【用户裁决 2026-08-17】恢复被软删的账号 + 查看"已删除账号"区。
     * 顶尖层专属；用户永不物理删除（审计按人归因，与"SKU 永不重用"同一纪律）。
     */
    USER_RESTORE: 'user.restore',
    SESSION_REVOKE: 'session.revoke',
    SETTINGS_MANAGE: 'settings.manage',
    OVERRIDE_PIN_MANAGE: 'override.pin.manage',
    /** M11：只读查看备份执行历史（02-ux-ia.md「数据 → 备份状态」），不含任何写操作 */
    BACKUP_VIEW: 'backup.view',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
/**
 * 需要 step-up 提权（PIN / 密码重输）的权限。
 * 拥有权限 ≠ 可以直接执行 —— 这些还需要一个 5 分钟有效、绑定单次操作的 elevation token。
 */
export const ELEVATED_PERMISSIONS = [
    PERMISSIONS.INVENTORY_OVERRIDE_NEGATIVE,
    PERMISSIONS.STOCKCOUNT_APPROVE,
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.USER_RESTORE,
    PERMISSIONS.OVERRIDE_PIN_MANAGE,
];
export function isElevatedPermission(p) {
    return ELEVATED_PERMISSIONS.includes(p);
}
// ---------------------------------------------------------------------------
// 预置角色（Master Spec §4）—— 角色是权限集合，不是代码中的 if 分支
// ---------------------------------------------------------------------------
export const SYSTEM_ROLES = {
    ADMIN: 'ADMIN',
    MANAGER: 'MANAGER',
    EMPLOYEE: 'EMPLOYEE',
    VIEWER: 'VIEWER',
};
const VIEWER_PERMISSIONS = [
    PERMISSIONS.WAREHOUSE_VIEW,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.SUPPLIER_VIEW,
    PERMISSIONS.REPORT_VIEW,
];
const EMPLOYEE_PERMISSIONS = [
    ...VIEWER_PERMISSIONS,
    PERMISSIONS.INVENTORY_INBOUND,
    PERMISSIONS.INVENTORY_OUTBOUND,
    PERMISSIONS.INVENTORY_TRANSFER,
    PERMISSIONS.STOCKCOUNT_CREATE,
    PERMISSIONS.ORDER_CREATE,
];
const MANAGER_PERMISSIONS = [
    ...EMPLOYEE_PERMISSIONS,
    PERMISSIONS.WAREHOUSE_MANAGE,
    PERMISSIONS.LAYOUT_EDIT,
    PERMISSIONS.LAYOUT_LEASE_OVERRIDE,
    PERMISSIONS.LOCATION_ARCHIVE,
    PERMISSIONS.PRODUCT_CREATE,
    PERMISSIONS.PRODUCT_EDIT,
    PERMISSIONS.INVENTORY_ADJUST,
    PERMISSIONS.INVENTORY_RESERVE,
    PERMISSIONS.INVENTORY_OVERRIDE_NEGATIVE,
    PERMISSIONS.INVENTORY_OVERRIDE_FEFO,
    PERMISSIONS.INVENTORY_EXPIRED_DISPOSE,
    PERMISSIONS.ORDER_CONFIRM,
    PERMISSIONS.ORDER_CANCEL,
    PERMISSIONS.RESERVATION_RELEASE,
    PERMISSIONS.SUPPLIER_MANAGE,
    PERMISSIONS.STOCKCOUNT_APPROVE,
    PERMISSIONS.ALERT_RULE_MANAGE,
    PERMISSIONS.ALERT_DISMISS_CRITICAL,
    PERMISSIONS.ALERT_MANAGE_OTHERS,
    PERMISSIONS.IMPORT_EXECUTE,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.REPORT_COST_VIEW,
    /** 【用户裁决 2026-08-17】管理人员：运营版审计（原 audit.view 收归顶尖层） */
    PERMISSIONS.AUDIT_VIEW_OPERATIONAL,
    /**
     * 【用户裁决 2026-08-17】管理人员可创建/删除普通用户。防提权约束在
     * users.service：只能管理"有效权限 ⊆ 自己"的账号、只能授出自己持有的权限
     * ——碰不到 ADMIN，也造不出比自己强的号。
     */
    PERMISSIONS.USER_MANAGE,
];
/**
 * 【用户裁决 2026-08-17】权限分三层展示（创建账号的选择页按此分组）：
 *   basic   普通人员预设的全部；
 *   manager 管理层增量（含普通层即为管理人员预设）；
 *   top     其余全部——顶尖专属，选择页默认灰锁，需顶尖身份密码重验解锁。
 * 三组互斥且并集 = ALL_PERMISSIONS（有契约测试钉住）。
 */
export const PERMISSION_TIERS = {
    basic: EMPLOYEE_PERMISSIONS,
    manager: MANAGER_PERMISSIONS.filter((p) => !EMPLOYEE_PERMISSIONS.includes(p)),
    top: ALL_PERMISSIONS.filter((p) => !MANAGER_PERMISSIONS.includes(p)),
};
/**
 * Master Spec §4：
 *   ADMIN    完全访问
 *   MANAGER  运营管理，含库存更正与供应商管理
 *   EMPLOYEE 按所授权限进行常规仓库作业
 *   VIEWER   只读
 */
export const DEFAULT_ROLE_PERMISSIONS = {
    ADMIN: ALL_PERMISSIONS,
    MANAGER: MANAGER_PERMISSIONS,
    EMPLOYEE: EMPLOYEE_PERMISSIONS,
    VIEWER: VIEWER_PERMISSIONS,
};
/**
 * 有效权限 = (角色权限 ∪ GRANT) − DENY。DENY 永远胜出。
 * 纯函数，前后端共用；后端是安全边界，前端仅用于渲染。
 */
export function resolveEffectivePermissions(src) {
    const effective = new Set(src.rolePermissions);
    for (const p of src.grants)
        effective.add(p);
    for (const p of src.denies)
        effective.delete(p);
    return effective;
}
//# sourceMappingURL=permissions.js.map